import { useState, useEffect, useRef } from 'react';

// Use this key to remember we're already requested this priceId
const getStorageKey = (priceId?: string) => `payment_intent_${priceId}`;

interface PaymentIntentResponse {
  clientSecret: string;
  subscriptionId: string;
}

// Add this function to allow clearing the cache
export const clearPaymentIntentCache = (priceId?: string) => {
  if (priceId) {
    sessionStorage.removeItem(getStorageKey(priceId));
  } else {
    // Clear all payment intent cache
    Object.keys(sessionStorage)
      .filter(k => k.startsWith('payment_intent_'))
      .forEach(key => sessionStorage.removeItem(key));
  }
  console.log("Cleared payment intent cache");
};

// Then modify the hook to accept a forceRefresh param
export const usePaymentIntent = (priceId: string | undefined, forceRefresh = false) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const requestInProgress = useRef(false);

  useEffect(() => {
    // Clear cache if forceRefresh
    if (forceRefresh) {
      clearPaymentIntentCache(priceId);
    }
    
    // First check session storage unless forceRefresh is true
    if (!forceRefresh) {
      const sessionKey = getStorageKey(priceId);
      const existingData = sessionStorage.getItem(sessionKey);
      
      if (existingData) {
        try {
          const data = JSON.parse(existingData);
          setClientSecret(data.clientSecret);
          setSubscriptionId(data.subscriptionId);
          setLoading(false);
          return; // Skip creating a new payment intent
        } catch (e) {
          // Continue if parsing fails
          sessionStorage.removeItem(sessionKey);
        }
      }
    }
    
    const createPaymentIntent = async () => {
      // Add this line to define sessionKey within this scope
      const sessionKey = getStorageKey(priceId);
      
      // Prevent duplicate requests using the ref
      if (requestInProgress.current) {
        console.log('⚠️ Request already in progress, skipping');
        return;
      }
      
      if (!priceId) {
        setError('No price ID provided');
        setLoading(false);
        return;
      }

      try {
        requestInProgress.current = true;
        // Get user's country from browser
        const userCountry = navigator.language?.split('-')[1] || 'US';
        
        const token = localStorage.getItem('token');
        const authHeader = token ? `Bearer ${token}` : '';

        const response = await fetch('/api/payment/create-payment-intent', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authHeader
          },
          body: JSON.stringify({
            priceId,
            countryCode: userCountry
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to create payment intent');
        }

        const data: PaymentIntentResponse = await response.json();
        setClientSecret(data.clientSecret);
        setSubscriptionId(data.subscriptionId);
        
        // Save to session storage when successful
        if (data.clientSecret && data.subscriptionId) {
          sessionStorage.setItem(sessionKey, JSON.stringify(data));
        }
        
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
        // Clear the in-flight flag after a delay
        setTimeout(() => {
          requestInProgress.current = false;
        }, 5000);
      }
    };

    createPaymentIntent();
    
    return () => {
      requestInProgress.current = false;
    };
  }, [priceId, forceRefresh]);

  return { clientSecret, subscriptionId, error, loading, refreshIntent: () => clearPaymentIntentCache(priceId) };
}; 